import { useMemo, useState } from 'react';
import { AlertTriangle, Check, ClipboardCheck, FileText, Flag, Plus, X } from 'lucide-react';

import { cn } from '../../lib/cn';
import { useSession } from '../../app/useSession';
import { useTeamMember } from '../../app/useTeamMember';
import {
  nextFindingRef,
  useDecisions,
  useFindings,
  useMilestones,
  useSaveFinding,
  type Finding,
  type FindingKind,
  type FindingStatus,
  type Severity,
} from './governanceQueries';
import styles from './governance.module.css';

/**
 * The governance board — what used to be three Notion databases.
 *
 * ADR-006 retired Notion-FIRST. The argument was not that Notion is bad; it is
 * that governing a project management tool from a different project management
 * tool means two places to look, and the second one is the one that stops
 * getting updated. The last four deliveries proved that empirically.
 *
 * This screen is deliberately a board and not a form-heavy admin. The only
 * thing it has to do well is answer the session-start question: what is open,
 * how bad is it, and what closes it.
 */

const SEVERITY: Record<Severity, string> = { p1: 'P1', p2: 'P2', p3: 'P3' };

const KIND: Record<FindingKind, string> = {
  security: 'אבטחה',
  architecture: 'ארכיטקטורה',
  risk: 'סיכון',
  question: 'שאלה פתוחה',
  'doc-drift': 'דריפט תיעוד',
  other: 'אחר',
};

const STATUS: Record<FindingStatus, string> = {
  open: 'פתוח',
  triaged: 'מוין',
  'in-progress': 'בעבודה',
  resolved: 'נסגר',
  'wont-fix': 'לא נתקן',
};

const STATUS_ORDER: FindingStatus[] = ['open', 'triaged', 'in-progress', 'resolved', 'wont-fix'];

export function GovernancePage() {
  const session = useSession();
  const sessionObj = session.status === 'signed-in' ? session.session : null;
  const userId = sessionObj?.user.id ?? null;
  const { data: teamMember } = useTeamMember(sessionObj);
  const orgId = teamMember?.organization_id ?? null;

  const { data: findings = [], isLoading } = useFindings(orgId);
  const { data: decisions = [] } = useDecisions(orgId);
  const { data: milestones = [] } = useMilestones(orgId);
  const saveFinding = useSaveFinding(orgId);

  const [showClosed, setShowClosed] = useState(false);
  const [draft, setDraft] = useState<Finding | null>(null);

  const shown = useMemo(
    () =>
      findings.filter((f) =>
        showClosed ? true : f.status !== 'resolved' && f.status !== 'wont-fix',
      ),
    [findings, showClosed],
  );
  const openCount = findings.filter(
    (f) => f.status !== 'resolved' && f.status !== 'wont-fix',
  ).length;
  const p1 = findings.filter(
    (f) => f.severity === 'p1' && f.status !== 'resolved' && f.status !== 'wont-fix',
  ).length;

  const startNew = () => {
    setDraft({
      id: '',
      ref: nextFindingRef(findings),
      title: '',
      detail: null,
      kind: 'other',
      severity: 'p3',
      status: 'open',
      location: null,
      resolved_when: null,
      resolved_at: null,
      updated_at: '',
    });
  };

  return (
    <div className={styles.page}>
      <header className={styles.head}>
        <div>
          <h1 className={styles.title}>
            <ClipboardCheck size={22} aria-hidden="true" /> ממשל
          </h1>
          <p className={styles.sub}>
            מה פתוח, כמה זה דחוף, ומה סוגר אותו. זו הקריאה שבתחילת סשן — ומה שהיה עד ADR-006
            בשלוש טבלאות ב-Notion.
          </p>
        </div>
        <div className={styles.counts}>
          <span className={cn(styles.count, p1 > 0 && styles.countHot)}>
            <AlertTriangle size={14} aria-hidden="true" /> {p1} P1
          </span>
          <span className={styles.count}>{openCount} פתוחים</span>
        </div>
      </header>

      {milestones.length > 0 ? (
        <section className={styles.strip} aria-label="אבני דרך">
          {milestones.map((m) => (
            <div key={m.id} className={cn(styles.milestone, styles[`ms_${m.status}`])}>
              <strong>
                <Flag size={13} aria-hidden="true" /> {m.title}
              </strong>
              {m.detail ? <span>{m.detail}</span> : null}
            </div>
          ))}
        </section>
      ) : null}

      <section aria-label="ממצאים">
        <div className={styles.sectionBar}>
          <h2>ממצאים</h2>
          <button
            type="button"
            className={cn(styles.chip, showClosed && styles.chipOn)}
            aria-pressed={showClosed}
            onClick={() => {
              setShowClosed(!showClosed);
            }}
          >
            הצג גם סגורים
          </button>
          <button type="button" className={styles.primary} onClick={startNew}>
            <Plus size={14} aria-hidden="true" /> ממצא חדש
          </button>
        </div>

        {draft ? (
          <FindingForm
            draft={draft}
            pending={saveFinding.isPending}
            onCancel={() => {
              setDraft(null);
            }}
            onChange={setDraft}
            onSave={() => {
              saveFinding.mutate(
                { ...draft, id: draft.id === '' ? null : draft.id, userId },
                {
                  onSuccess: () => {
                    setDraft(null);
                  },
                },
              );
            }}
          />
        ) : null}

        {isLoading ? <p className={styles.muted}>טוען…</p> : null}
        {!isLoading && shown.length === 0 ? (
          <p className={styles.muted}>
            אין ממצאים פתוחים. זה או מצוין, או סימן שאף אחד לא רושם אותם.
          </p>
        ) : null}

        <ul className={styles.list}>
          {shown.map((f) => {
            const closed = f.status === 'resolved' || f.status === 'wont-fix';
            return (
              <li key={f.id} className={cn(styles.card, closed && styles.cardDone)}>
                <div className={styles.cardHead}>
                  <span className={cn(styles.sev, styles[`sev_${f.severity}`])}>
                    {SEVERITY[f.severity]}
                  </span>
                  <span className={styles.ref}>{f.ref}</span>
                  <strong className={styles.cardTitle}>{f.title}</strong>
                  <span className={styles.kind}>{KIND[f.kind]}</span>
                  <button
                    type="button"
                    className={styles.chip}
                    onClick={() => {
                      setDraft(f);
                    }}
                  >
                    ערוך
                  </button>
                </div>
                {f.detail ? <p className={styles.detail}>{f.detail}</p> : null}
                <div className={styles.meta}>
                  <span className={cn(styles.status, closed && styles.statusDone)}>
                    {closed ? <Check size={12} aria-hidden="true" /> : null}
                    {STATUS[f.status]}
                  </span>
                  {f.location ? <code className={styles.loc}>{f.location}</code> : null}
                  {/* The exit condition is the field that decides whether a
                      finding ever closes, so it is shown, not hidden behind an
                      edit click. */}
                  {f.resolved_when ? (
                    <span className={styles.when}>נסגר כאשר: {f.resolved_when}</span>
                  ) : (
                    <span className={styles.whenMissing}>אין תנאי סגירה</span>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      </section>

      <section aria-label="החלטות" className={styles.decisions}>
        <div className={styles.sectionBar}>
          <h2>החלטות</h2>
          <span className={styles.muted}>הנימוק חי ב-git. כאן רק הסטטוס והקישור.</span>
        </div>
        <ul className={styles.decisionList}>
          {decisions.map((d) => (
            <li key={d.id} className={cn(d.status === 'superseded' && styles.superseded)}>
              <span className={styles.ref}>{d.ref}</span>
              <span className={styles.decisionTitle}>{d.title}</span>
              {d.doc_path ? (
                <code className={styles.loc}>
                  <FileText size={11} aria-hidden="true" /> {d.doc_path}
                </code>
              ) : null}
              <span className={styles.decisionStatus}>
                {d.status === 'superseded' ? 'הוחלף' : d.status === 'accepted' ? 'מאושר' : d.status}
              </span>
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}

function FindingForm({
  draft,
  pending,
  onChange,
  onSave,
  onCancel,
}: {
  draft: Finding;
  pending: boolean;
  onChange: (next: Finding) => void;
  onSave: () => void;
  onCancel: () => void;
}) {
  return (
    <div className={styles.form}>
      <div className={styles.formRow}>
        <input
          type="text"
          className={styles.input}
          value={draft.ref}
          aria-label="מזהה"
          onChange={(e) => {
            onChange({ ...draft, ref: e.target.value });
          }}
        />
        <input
          type="text"
          className={cn(styles.input, styles.grow)}
          value={draft.title}
          aria-label="כותרת הממצא"
          placeholder="מה שגוי, במשפט אחד"
          onChange={(e) => {
            onChange({ ...draft, title: e.target.value });
          }}
        />
      </div>

      <textarea
        className={styles.textarea}
        rows={3}
        value={draft.detail ?? ''}
        aria-label="פירוט"
        placeholder="מה בדיוק קורה, ולמה זה משנה"
        onChange={(e) => {
          onChange({ ...draft, detail: e.target.value || null });
        }}
      />

      <div className={styles.formRow}>
        <select
          className={styles.select}
          value={draft.severity}
          aria-label="חומרה"
          onChange={(e) => {
            onChange({ ...draft, severity: e.target.value as Severity });
          }}
        >
          {(Object.keys(SEVERITY) as Severity[]).map((s) => (
            <option key={s} value={s}>
              {SEVERITY[s]}
            </option>
          ))}
        </select>
        <select
          className={styles.select}
          value={draft.kind}
          aria-label="סוג"
          onChange={(e) => {
            onChange({ ...draft, kind: e.target.value as FindingKind });
          }}
        >
          {(Object.keys(KIND) as FindingKind[]).map((k) => (
            <option key={k} value={k}>
              {KIND[k]}
            </option>
          ))}
        </select>
        <select
          className={styles.select}
          value={draft.status}
          aria-label="סטטוס"
          onChange={(e) => {
            onChange({ ...draft, status: e.target.value as FindingStatus });
          }}
        >
          {STATUS_ORDER.map((s) => (
            <option key={s} value={s}>
              {STATUS[s]}
            </option>
          ))}
        </select>
        <input
          type="text"
          className={cn(styles.input, styles.grow)}
          value={draft.location ?? ''}
          aria-label="מיקום"
          placeholder="קובץ, טבלה או כתובת"
          onChange={(e) => {
            onChange({ ...draft, location: e.target.value || null });
          }}
        />
      </div>

      <input
        type="text"
        className={styles.input}
        value={draft.resolved_when ?? ''}
        aria-label="תנאי סגירה"
        placeholder="מה צריך להיות נכון כדי לסגור — בלי זה הממצא רק מזדקן"
        onChange={(e) => {
          onChange({ ...draft, resolved_when: e.target.value || null });
        }}
      />

      <div className={styles.formRow}>
        <button
          type="button"
          className={styles.primary}
          disabled={draft.title.trim().length === 0 || pending}
          onClick={onSave}
        >
          <Check size={14} aria-hidden="true" /> שמור
        </button>
        <button type="button" className={styles.chip} onClick={onCancel}>
          <X size={13} aria-hidden="true" /> בטל
        </button>
      </div>
    </div>
  );
}
